// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Hearsay — what one seat believes about everyone else's testimony.
//
// This is the deduction, written down. It takes exactly what a human player at this table has —
// the public event stream, the fixed deck composition, and the private half-facts your own cards
// bought you — and returns, for each rival, a probability over the eight ranks.
//
// IT IS SHARED BY THE BOT AND BY THE SCREEN. The player's docket panel is rendered from the same
// numbers the bot decides on, which is the only honest way to build a deduction game: if the bot
// knows something the interface will not show you, the game is cheating.
//
// It never reads a hidden value. Its only inputs are PublicState and this seat's own PrivateView.

import { docket, type Learned, type PublicEv, type PublicState } from './game';

/** Index 1..8. Sums to 1 when the seat holds a card, all zero when they hold none. */
export type Belief = number[];

const RANKS = 8;

const empty = (): Belief => new Array<number>(RANKS + 1).fill(0);

function normalise(b: Belief): Belief {
  const total = b.reduce((a, x) => a + x, 0);
  if (total <= 0) return empty();
  return b.map((x) => x / total);
}

/**
 * How the certainty a seat has about a rival decays.
 *
 * `exact` survives until that rival plays the card you saw, is made to redraw, or trades. Working
 * that out is a pure walk of the PUBLIC stream, which is what makes it legal: every peer, and every
 * spectator, computes the same answer from the same bytes.
 */
interface Track {
  /** Ranks known for certain, or null once the knowledge went stale. */
  exact: number[] | null;
  /** A private lower/upper bound bought with a missed call. */
  above: number;
  below: number;
}

function freshTrack(): Track {
  return { exact: null, above: 0, below: RANKS + 1 };
}

/**
 * Replay the public stream from a given ply, deciding whether knowledge about `seat` survived it.
 *
 * The rule is deliberately conservative: anything that could have changed what they hold clears the
 * certainty. Being conservative costs the bot a little strength and costs correctness nothing, and
 * an over-confident belief is exactly the bug that would be invisible in every test.
 */
function survives(events: readonly PublicEv[], seat: string, sinceStamp: number, exact: number[]): number[] | null {
  let stamp = 0;
  const held = exact.slice();
  for (const e of events) {
    if (e.t === 'play' || e.t === 'noeffect' || e.t === 'out') stamp = e.ply;
    if (stamp <= sinceStamp) continue;
    if (e.t === 'play' && e.by === seat) {
      // They played SOMETHING. If it is a rank I knew they held, that copy has left their hand and
      // what replaced it is unknown to me. If it is not — they played the card they had just drawn,
      // and everything I knew is still sitting in front of them.
      const i = held.indexOf(e.rank);
      if (i >= 0) {
        held.splice(i, 1);
        if (held.length === 0) return null;
      }
    } else if (e.t === 'redraw' && e.target === seat) {
      return null;
    } else if (e.t === 'swap' && (e.a === seat || e.b === seat)) {
      return null;
    }
  }
  return held;
}

/** Whether anything at all could have changed what `seat` holds since `sinceStamp`. */
function untouched(events: readonly PublicEv[], seat: string, sinceStamp: number): boolean {
  let stamp = 0;
  for (const e of events) {
    if (e.t === 'play' || e.t === 'noeffect' || e.t === 'out') stamp = e.ply;
    if (stamp <= sinceStamp) continue;
    if (e.t === 'play' && e.by === seat) return false;
    if (e.t === 'redraw' && e.target === seat) return false;
    if (e.t === 'swap' && (e.a === seat || e.b === seat)) return false;
  }
  return true;
}

export interface BeliefOpts {
  /**
   * When false the belief ignores the docket AND everything this seat has learned, and treats every
   * rank as equally likely.
   *
   * This is the CONTROL ARM. tests/balance.test.ts plays a bot with this switched off against the
   * same bot with it on, and requires the gap to be decisively negative. A deduction game where
   * deducting does not measurably win is a deduction game with no deduction in it, and no amount of
   * looking at the screen would ever tell you.
   */
  useDocket: boolean;
}

/**
 * Beliefs about every seat, for the seat `me`.
 *
 * Returns an entry per seat id; a dead seat, and `me`, are all-zero.
 */
export function beliefs(
  p: PublicState,
  me: string,
  myHand: readonly number[],
  learned: readonly Learned[],
  opts: BeliefOpts,
): Record<string, Belief> {
  const out: Record<string, Belief> = {};

  // The prior: everything not yet on the table and not in my own hand, weighted by how many copies
  // of each rank are left. This is exactly the docket panel the player is shown.
  const pool = opts.useDocket ? docket(p) : new Array<number>(RANKS + 1).fill(1);
  const prior = empty();
  for (let r = 1; r <= RANKS; r++) prior[r] = Math.max(0, (pool[r] ?? 0) - (opts.useDocket ? countOf(myHand, r) : 0));

  const tracks = new Map<string, Track>();
  for (const seat of p.seats) tracks.set(seat.id, freshTrack());

  if (opts.useDocket) {
    for (const l of learned) {
      const t = tracks.get(l.seat);
      if (!t) continue;
      if (l.ranks) {
        const alive = survives(p.events, l.seat, l.ply, l.ranks);
        if (alive && alive.length) t.exact = alive;
      } else if (l.bound && untouched(p.events, l.seat, l.ply)) {
        // A bound is a fact about what they held THEN. The moment anything could have changed their
        // hand it is worthless, and holding on to it would be the one bug no test would ever see.
        if (l.bound.hint === 'higher') t.above = Math.max(t.above, l.bound.named);
        else t.below = Math.min(t.below, l.bound.named);
      }
    }
  }

  for (const seat of p.seats) {
    if (!seat.alive || seat.id === me || seat.handCount === 0) {
      out[seat.id] = empty();
      continue;
    }
    const t = tracks.get(seat.id)!;
    if (t.exact && t.exact.length) {
      const b = empty();
      for (const r of t.exact) b[r] += 1;
      out[seat.id] = normalise(b);
      continue;
    }
    const b = empty();
    for (let r = 1; r <= RANKS; r++) {
      if (prior[r] <= 0) continue;
      // A missed call rules out the named rank outright as well as bounding the side.
      if (r <= t.above || r >= t.below) continue;
      b[r] = prior[r];
    }
    // A bound that eliminated everything means the read has gone stale (they drew since). Fall back
    // to the unbounded prior rather than asserting a hand nobody can be holding.
    out[seat.id] = normalise(b.some((x) => x > 0) ? b : prior.slice());
  }
  return out;
}

const countOf = (hand: readonly number[], rank: number): number => hand.filter((r) => r === rank).length;

/** P(this seat is holding exactly this rank). */
export const chanceOf = (b: Belief, rank: number): number => b[rank] ?? 0;

/** P(this seat's testimony beats `rank`). Ties are not a win for either side. */
export function chanceAbove(b: Belief, rank: number): number {
  let p = 0;
  for (let r = rank + 1; r <= RANKS; r++) p += b[r] ?? 0;
  return p;
}

/** The rank a caller should name to learn the most when they miss — the median of the belief mass. */
export function medianRank(b: Belief): number {
  let acc = 0;
  for (let r = 1; r <= RANKS; r++) {
    acc += b[r] ?? 0;
    if (acc >= 0.5) return r;
  }
  return RANKS;
}

/**
 * What this seat can honestly say it knows about a rival, in three words.
 *
 * Rendered on the rival's row, and it is not decoration: a missed call buys the caller a private
 * halving of somebody's possibility space, and without this the player who SPENT THE TURN buying it
 * would never see what they bought. The whole anti-luck argument for the Bailiff rests on the read
 * being usable, and a read you cannot see is not usable.
 *
 * It is derived from the same belief vector the bot decides on, so the interface cannot know less
 * than the opponent does.
 */
export function readLabel(b: Belief): { text: string; sure: boolean } | null {
  const live: number[] = [];
  for (let r = 1; r <= RANKS; r++) if ((b[r] ?? 0) > 0) live.push(r);
  if (live.length === 0) return null;
  if (live.length === 1) return { text: `holds the ${live[0]}`, sure: true };
  // A span from 1 to 8 is not a read, it is the absence of one, and printing it as though it were
  // makes the row that DOES carry a read harder to spot.
  if (live[0] === 1 && live[live.length - 1] === RANKS) return null;
  return { text: `somewhere in ${live[0]}–${live[live.length - 1]}`, sure: false };
}

/** Shannon entropy in bits — how much doubt is left about one seat. */
export function entropy(b: Belief): number {
  let h = 0;
  for (let r = 1; r <= RANKS; r++) {
    const p = b[r] ?? 0;
    if (p > 0) h -= p * Math.log2(p);
  }
  return h;
}
